import type * as React from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { AnimatedNumber } from "./AnimatedNumber";
import type { Domain } from "./Tile";

interface SensorBadgeProps extends Omit<React.ComponentProps<"span">, "children"> {
  icon: ReactNode;
  value: number;
  unit?: string;
  precision?: number;
  domain?: Domain;
}

const domainText: Record<Domain, string> = {
  neutral: "text-muted-foreground",
  light: "text-domain-light",
  climate: "text-domain-climate",
  energy: "text-domain-energy",
  security: "text-domain-security",
  flow: "text-domain-flow",
};

/** Read-only sensor value chip (temperature, humidity, motion, contact). */
export function SensorBadge({
  icon,
  value,
  unit,
  precision = 1,
  domain = "neutral",
  className,
  ...props
}: SensorBadgeProps) {
  return (
    <span
      data-slot="sensor-badge"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full bg-tile px-2.5 py-1 text-xs font-medium text-tile-foreground shadow-tile [&_svg]:size-3.5",
        className,
      )}
      {...props}
    >
      <span className={domainText[domain]}>{icon}</span>
      <span className="tabular-nums">
        <AnimatedNumber value={value} precision={precision} />
        {unit && <span className="ml-0.5 text-muted-foreground">{unit}</span>}
      </span>
    </span>
  );
}
